
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

interface EarlyAccessLead {
  id: string;
  created_at: string;
  first_name: string;
  email: string;
  motivation: string;
  motivation_other?: string;
  experience: string;
  current_habit_app?: string;
  challenge: string;
  use_case: string;
  use_case_other?: string;
  interview_interest: string;
}

interface LeadsTableProps {
  leads: EarlyAccessLead[];
  totalLeads: number;
  onDeleteLead: (id: string) => Promise<void>;
  deleting: string | null;
  clearFilters: () => void;
}

export const LeadsTable = ({ 
  leads, 
  totalLeads, 
  onDeleteLead, 
  deleting, 
  clearFilters 
}: LeadsTableProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Records</CardTitle>
        <CardDescription>
          Complete list of users registered for early access
        </CardDescription>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-8">
            {totalLeads === 0 ? (
              <>
                <p className="text-gray-500 mb-4">No records yet</p>
                <p className="text-sm text-gray-400">
                  Records will appear here when users complete the form
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-500 mb-4">No records match your filters</p>
                <Button onClick={clearFilters} variant="outline">
                  Clear Filters
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Motivation</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Challenge</TableHead>
                  <TableHead>Use Case</TableHead>
                  <TableHead>Interview</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">
                      {formatDate(lead.created_at)}
                    </TableCell>
                    <TableCell>{lead.first_name}</TableCell>
                    <TableCell>{lead.email}</TableCell>
                    <TableCell className="max-w-xs truncate" title={lead.motivation}>
                      {lead.motivation}
                      {lead.motivation_other && (
                        <div className="text-xs text-gray-500 mt-1">
                          Other: {lead.motivation_other}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={lead.experience}>
                      {lead.experience}
                      {lead.current_habit_app && (
                        <div className="text-xs text-gray-500 mt-1">
                          App: {lead.current_habit_app}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={lead.challenge}>
                      {lead.challenge}
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={lead.use_case}>
                      {lead.use_case}
                      {lead.use_case_other && (
                        <div className="text-xs text-gray-500 mt-1">
                          Other: {lead.use_case_other}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={lead.interview_interest === "Yes, I'm interested" ? "default" : "secondary"}>
                        {lead.interview_interest === "Yes, I'm interested" ? "Interested" : "Not interested"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={deleting === lead.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Record</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete the record for {lead.first_name} ({lead.email})? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDeleteLead(lead.id)}
                              disabled={deleting === lead.id}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {deleting === lead.id ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
